import { Document, DocumentUser } from 'apis/entities/document.entity'
import SidebarLayout from 'components/SidebarLayout'
import { useApi } from 'providers/ApiProvider'
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  MutableRefObject,
} from 'react'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import { resolveRelativeUrls } from 'utils/HtmlUtil'
import DropDownMenu, { DropDownItem } from 'views/DropDownMenu'
import SearchInput from 'views/SearchInput'
import Spinner from 'views/Spinner'
import sortSvg from 'images/sort.svg'
import sort2Svg from 'images/sort2.svg'
import plusSvg from 'images/quiz-plus.svg'
import minusSvg from 'images/quiz-minus.svg'
import lockSvg from 'images/lock.svg'
import unlockSvg from 'images/unlock.svg'
import close2 from 'images/close2.svg'
import ellipsisSvg from 'images/ellipsis.svg'
import { Tooltip } from 'react-tooltip'
import Switch from '@mui/material/Switch'
import hasNotesSvg from 'images/has-notes.svg'
import noNotesSvg from 'images/no-notes.svg'
import { DatasetSmall, getFamV4, deleteTopic } from '../mocks/MockTopicData'
import { styled, Menu, MenuItem } from '@mui/material'
import React from 'react'
import WidgetArchCopy from 'views/WidgetArchCopy'
import Emitter, { Events } from 'core/emitter'
import ButtonSpinner from 'views/ButtonSpinner'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'

enum SortBy {
  Topic = 'topic',
  PublishedBy = 'publishedUserName',
  DateTime = 'createdAt',
  Length = 'time',
  SearchSimilarity = 'searchSimilarity',
}

type Direction = 'ascending' | 'descending' | ''
const PAGE_SIZE = 100
const publishedByOptionLabel = {
  id: '0',
  name: 'Editor',
  value: '0',
  isLabel: true,
}

const publishedByOptionShowAll = {
  id: '1',
  name: 'Show all',
  value: '',
  isLabel: false,
}

const tooltipStyle = {
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '6px',
}

const tooltipStyleReverse = {
  backgroundColor: '#6b69c1',
  color: '#fff',
  borderRadius: '6px',
}

const REACT_APP_PUBLISH_URL = process.env.REACT_APP_PUBLISH_URL
const REACT_APP_PREVIEW_URL = process.env.REACT_APP_PREVIEW_URL

let INIT_DOCS: Document[] = [...DatasetSmall]

function isFakeData(): boolean {
  return window.location.search.includes('fake=true')
}

// Gates the DEBUG column (intV1 -> Word/JSON buttons) — add
// ?debug=true to the URL to see it.
function isDebug(): boolean {
  return window.location.search.includes('debug=true')
}

function isTopicNesting(): boolean {
  if (window.location.search.includes('nesting=true')) return true
  if (window.location.search.includes('nesting=false')) return false
  return false
}

function isReverseHistory(): boolean {
  if (window.location.search.includes('reverseHistory=true')) return true
  if (window.location.search.includes('reverseHistory=false')) return false
  return true
}

function getAlternatingStyle() {
  let isHeader1 = true
  let isCell1 = true

  return (what: string, startRow = false) => {
    if (what === 'td') {
      if (startRow) isCell1 = true
      else isCell1 = !isCell1
    } else {
      if (startRow) isHeader1 = true
      else isHeader1 = !isHeader1
    }
    if (what === 'td') {
      return isCell1 ? 'mentem-table-cell-2' : 'mentem-table-cell-1'
    }
    return isHeader1
      ? StyleUtil.stickyTableHeader1
      : StyleUtil.stickyTableHeader2
  }
}

const NotesModal = ({
  isOpen,
  onClose,
  initialNotes,
  onSave,
}: {
  isOpen: boolean
  onClose: () => void
  initialNotes: string
  onSave: (notes: string) => void
}) => {
  const [notes, setNotes] = useState(initialNotes)

  const handleSave = () => {
    onSave(notes)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      data-testid="notes-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '24px',
          boxShadow: `0px 381px 107px 0px rgba(0, 0, 0, 0.00), 
            0px 244px 98px 0px rgba(0, 0, 0, 0.01), 
            0px 137px 82px 0px rgba(0, 0, 0, 0.05), 
            0px 61px 61px 0px rgba(0, 0, 0, 0.09), 
            0px 15px 34px 0px rgba(0, 0, 0, 0.10)`,
        }}
      >
        <div
          data-testid="notes-modal-close"
          style={{
            cursor: 'pointer', // Change cursor to pointer
            display: 'flex',
            justifyContent: 'flex-end',
            paddingBottom: 24,
          }}
          onClick={onClose} // Call onClose when the close button is clicked
        >
          <img src={close2} alt="Close" style={{ height: 24 }} />
        </div>
        <textarea
          data-testid="notes-modal-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{
            width: '500px',
            height: '96px',
            border: '1px solid #D5D5DB',
            paddingLeft: 12,
            paddingTop: 6,
            color: '#92929D',
            fontSize: '14px',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: '20px',
            fontStyle: 'normal',
            outline: 'none',
          }}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault() // Prevent the default action to avoid adding a new line
              handleSave() // Call the save function
            }
          }}
          placeholder="Type notes here"
        />
        <div
          style={{
            marginTop: 18,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            data-testid="notes-modal-save-button"
            className={StyleUtil.buttonPrimaryUnsw}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

function Topics() {
  const { isLogged, getRole, user } = useCompositeAuth()
  const role = getRole()
  const canUpdateCatalog =
    role === Role.SuperAdmin ||
    role === Role.Admin ||
    role === Role.Author ||
    role === Role.Designer
  const canDeleteCatalog = role === Role.SuperAdmin
  const {
    getDocumentsV2,
    getDocumentPublishedBy,
    deleteDocument,
    updateDocument,
    publish,
    getDocument,
    getDocumentHtml,
    downloadIntV1AsWord,
    viewIntV1Json,
  } = useApi()

  // Contains ONLY shown topics
  const [tableTopics, setTableTopics] = useState<Document[]>([])
  // Contains ALL topics
  const [topicsDB, setTopicsDB] = useState<Document[]>([])
  const [cacheDocuments, setCacheDocuments] = useState<any[]>([]) // For the tooltip
  const [publishedByOptions, setPublishedByOptions] = useState<any>([
    publishedByOptionLabel,
  ])
  const [isClickDelete, setIsClickDelete] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const refSearchTextInput = useRef<string>('')
  const refStartDate = useRef<Date | undefined>(undefined)
  const refPublishedBy = useRef<string>('')
  const refPublished = useRef<boolean | null>(true)
  const refClient = useRef<string>('')
  const refClientSpecific = useRef<boolean | null>(false)
  const refIsTopic = useRef<boolean | null>(true)
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [nResults, setNResults] = useState<number>(0)
  const [currPage, setCurrPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(
    parseInt(
      new URLSearchParams(window.location.search).get('pageSize') ||
        PAGE_SIZE.toString(),
    ),
  )
  const [isFetchedPublishedBy, setIsFetchedPublishedBy] =
    useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [checkedIds, setCheckedIds] = useState<string[]>([])
  const [sortConfig, setSortConfig] = useState<{
    key: SortBy
    direction: Direction
  } | null>({ key: SortBy.DateTime, direction: 'descending' })
  const [orderByTitle, setOrderByTitle] = useState<Direction>('')
  const [orderByPublishedBy, setOrderByPublishedBy] = useState<Direction>('')
  const [orderByDateTime, setOrderByDateTime] = useState<Direction>('')
  const [orderByLength, setOrderByLength] = useState<Direction>('')

  const alternateStyle = getAlternatingStyle()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDoc, setEditingDoc] = useState<Document | null>(null)
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null)
  const [morePopupDocId, setMorePopupDocId] = useState<string>('')
  const [showWidgetArchCopy, setShowWidgetArchCopy] = useState(false)
  const [copyDocumentIds, setCopyDocumentIds] = useState<string[] | undefined>(
    undefined,
  )
  const [copyIsEnterprise, setCopyIsEnterprise] = useState(true)

  const [notesTooltip, setNotesTooltip] = useState<{
    visible: boolean
    content: string
    x: number
    y: number
  }>({ visible: false, content: '', x: 0, y: 0 })
  const tooltipRef = useRef<HTMLDivElement>(null)

  const fetchDocument = useCallback(
    async (id: string) => {
      // If exist in cache, return
      if (cacheDocuments.find((d) => d.id === id)) {
        return
      }

      const doc = await getDocument(id)
      if (doc) {
        // Add if new or update if existing
        setCacheDocuments((prev) => {
          const index = prev.findIndex((d) => d.id === id)
          if (index === -1) {
            return [...prev, doc]
          } else {
            prev[index] = doc
            return prev
          }
        })
      }
    },
    [cacheDocuments, getDocument],
  )

  const hasArchTopics = useCallback(
    (id: string): boolean => {
      const doc = cacheDocuments.find((d) => d.id === id)
      if (!doc) return false
      if (!doc.topics) return false
      return doc.topics.length > 0
    },
    [cacheDocuments],
  )

  const renderTooltipContentForTopics = useCallback(
    (id: string) => {
      const doc = cacheDocuments.find((d) => d.id === id)
      if (!doc) return <div></div>
      if (!doc.topics) return <div></div>
      const topics = doc.topics
      if (topics.length === 0) return <div></div>
      return (
        <div>
          <ul>
            {topics
              .filter((topic: any) => topic.relationship)
              .map((topic: any, index: any) => (
                <li key={index} className="mb-2">
                  <p>{`Topic ${topic.topicNumber} from ${topic.relationship.intakeName} ${topic.relationship.courseName} for ${topic.relationship.clientName}`}</p>
                </li>
              ))}
          </ul>
        </div>
      )
    },
    [cacheDocuments],
  )

  function getSwitchTheme(ref?: MutableRefObject<boolean | null>) {
    return styled(Switch)(({ theme }) => ({
      padding: 9,
      width: 54,
      '& .MuiButtonBase-root': {
        '&.Mui-checked': {
          transform: 'translateX(16px)',
        },
        '& .MuiSwitch-thumb': {
          boxShadow: 'none',
          width: 12,
          height: 12,
          margin: 4,
          ...(ref?.current === false && {
            backgroundColor: '#8A68C8', // --tertiary-purple
          }),
        },
      },
      '& .MuiSwitch-track': {
        borderRadius: 22 / 2,
        border: '1px solid black',
        ...(ref?.current
          ? {
              background: '#8A68C8', // --tertiary-purple
              opacity: '1 !important',
            }
          : {
              background: 'white',
              opacity: '1 !important',
            }),
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 16,
          height: 16,
        },
        '&::before': {
          left: 12,
        },
        '&::after': {
          right: 12,
        },
      },
    }))
  }

  const SwitchPublished = getSwitchTheme(refPublished)
  const SwitchIsTopic = getSwitchTheme(refIsTopic)
  const SwitchClientSpecific = getSwitchTheme(refClientSpecific)

  const sortedDocuments = useMemo(() => {
    let sortableItems = tableTopics?.length ? [...tableTopics] : []

    if (sortConfig !== null) {
      if (isReverseHistory()) {
        let items = []
        for (const doc of sortableItems) {
          const rows = [doc, ...(doc.children || [])]
          // Sort rows by createdAt
          rows.sort((a, b) => {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
          })
          items.push({
            ...rows[0],
            children: rows.slice(1).map((childDoc) => {
              return { ...childDoc, children: [] }
            }),
          })
        }
        sortableItems = items
      }
      sortableItems.sort((a: Document, b: Document) => {
        if (sortConfig.key === SortBy.SearchSimilarity) {
          if (a.nameSimilarity === b.nameSimilarity) {
            return 0
          } else if (a.nameSimilarity < b.nameSimilarity) {
            return 1
          } else {
            return -1
          }
        } else {
          const rawA = a[sortConfig.key]
          const rawB = b[sortConfig.key]

          // Handle null/undefined values - put them at the end
          if (rawA == null && rawB == null) return 0
          if (rawA == null) return 1
          if (rawB == null) return -1

          const valA = typeof rawA === 'string' ? rawA.toLowerCase() : rawA
          const valB = typeof rawB === 'string' ? rawB.toLowerCase() : rawB

          if (valA < valB) {
            return sortConfig.direction === 'ascending' ? -1 : 1
          }
          if (valA > valB) {
            return sortConfig.direction === 'ascending' ? 1 : -1
          }
          return 0
        }
      })
    }
    return sortableItems
  }, [tableTopics, sortConfig])

  useEffect(() => {
    const { masterDocs } = getFamV4(topicsDB, isTopicNesting())

    // Sort masterDocs newest first
    masterDocs.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    setTableTopics(masterDocs)
  }, [topicsDB])

  const searchDocuments = useCallback(
    async (_currPage?: number, _pageSize?: number) => {
      _currPage = _currPage || currPage
      _pageSize = _pageSize || pageSize
      console.log(`refPublishedBy.current=${refPublishedBy.current}`)
      let filter = `skunkworks=1&publishedBy=${refPublishedBy.current}`
      if (refPublished.current != null)
        filter += `&published=${refPublished.current ? 1 : 0}`
      if (refClient.current) filter += `&theme=${refClient.current}`

      filter += `&clientSpecific=${refClientSpecific.current ? 1 : 0}`
      filter += `&isTopic=${refIsTopic.current ? 1 : 0}`

      const search = refSearchTextInput.current
      setIsSearching(true)
      let docs: Document[] = []
      if (isFakeData()) {
        const start = (_currPage - 1) * _pageSize
        const end = start + _pageSize
        docs = INIT_DOCS.filter((x) => {
          if (refPublished.current !== x.published) {
            return false
          }
          if (refIsTopic.current !== x.isTopic) {
            return false
          }
          if (refClientSpecific.current !== x.clientSpecific) {
            return false
          }
          return true
        }) as Document[]
        setNResults(docs.length)
        docs = docs.slice(start, end)
      } else {
        try {
          const result = await getDocumentsV2(
            filter,
            search,
            refStartDate.current,
            undefined,
            _pageSize,
            _currPage,
          )
          if (result && result.items) {
            docs = result.items as Document[]
            setNResults(result.totalItems)
          }
        } catch (error) {
          console.error('searchDocuments', error)
        }
      }

      // Fix duplicate records
      setTopicsDB((x) => {
        const updatedDocs = docs.map((doc) => ({
          ...doc,
          _TEMP_PARENT_ID: doc.parentIds[0] || null,
        }))

        const map = new Map()

        // Add all items from updatedDocs
        updatedDocs.forEach((item) => map.set(item.id, item))

        // Add all items from x
        x.forEach((item) => map.set(item.id, item))

        // Convert back to an array
        return Array.from(map.values())
      })

      setIsSearching(false)
    },
    [currPage, getDocumentsV2, pageSize],
  )

  const fetchPublishedByUsers = useCallback(async () => {
    setIsFetchedPublishedBy(false)
    const users = await getDocumentPublishedBy()
    let allOptions = [publishedByOptionLabel]
    if (users) {
      let options = users.map((u: DocumentUser, _index: number) => {
        return {
          id: u.publishedUserId,
          name: u.publishedUserName,
          value: u.publishedUserId,
          isLabel: false,
        }
      })
      allOptions = allOptions.concat(options)
      allOptions.push(publishedByOptionShowAll)
      setPublishedByOptions(allOptions)
      setIsFetchedPublishedBy(true)
    }
  }, [getDocumentPublishedBy])

  const isFetched = (): boolean => {
    return isFetchedPublishedBy
  }

  const isSuperAdmin = useCallback((): boolean => {
    return getRole() === Role.SuperAdmin
  }, [getRole])

  const showUserTopicsOnly = useCallback((): boolean => {
    return getRole() === Role.Contractor
  }, [getRole])

  // All roles (Admin, SuperAdmin, Author, Designer, Contractor) can see publish column in preview mode
  const hasPublishColumn = useCallback((): boolean => {
    const role = getRole()
    return (
      (role === Role.Admin ||
        role === Role.SuperAdmin ||
        role === Role.Author ||
        role === Role.Designer ||
        role === Role.Contractor) &&
      refPublished.current === false
    )
  }, [getRole])

  // only super admins can manage topics
  const canManageTopics = useCallback((): boolean => {
    return isSuperAdmin()
  }, [isSuperAdmin])

  const onInputChangedSearch = (value: string) => {
    refSearchTextInput.current = value
  }

  const onEnterSearch = async () => {
    setCurrPage(1)
    setTopicsDB([])
    setSortConfig({ key: SortBy.SearchSimilarity, direction: 'descending' })
    await searchDocuments(1)
  }

  const onSelectClient = async (item: DropDownItem) => {
    const value = item.value
    refClient.current = value
    setCurrPage(1)
    setTopicsDB([])
    await searchDocuments(1)
  }

  const onSelectDate = async (item: DropDownItem) => {
    const value = item.value
    if (value === undefined) {
      // Show all
      refStartDate.current = undefined
    } else if (value > 0) {
      const startDate = new Date()
      // Calculate start date by value which is number of days
      startDate.setDate(startDate.getDate() - value)
      refStartDate.current = startDate
    }
    setCurrPage(1)
    setTopicsDB([])
    await searchDocuments(1)
  }

  const onSelectPublishedBy = async (item: DropDownItem) => {
    const value = item.value
    refPublishedBy.current = value
    setCurrPage(1)
    setTopicsDB([])
    await searchDocuments(1)
  }

  const onClickDelete = () => {
    if (checkedIds.length === 0) {
      ToastUtil.warning('Please select at least one page')
    } else {
      setIsClickDelete(true)
    }
  }

  const onChangeCheckbox = useCallback(
    (checked: boolean, id: string) => {
      setIsClickDelete(false)
      if (checked) {
        setCheckedIds([...checkedIds, id])
      } else {
        setCheckedIds(checkedIds.filter((y) => y !== id))
      }
    },
    [checkedIds],
  )

  const onConfirmDelete = async () => {
    setIsLoading(true)

    for (const x of checkedIds) {
      if (isFakeData()) {
        INIT_DOCS = deleteTopic(INIT_DOCS as Document[], x) as Document[]
      } else {
        await deleteDocument(x)
      }
    }
    setTopicsDB([])
    await searchDocuments()
    setIsLoading(false)
    ToastUtil.success(`Deleted successfully`)

    document.querySelectorAll('[data-id="checkbox"]').forEach((x: any) => {
      x.checked = false
    })
    setCheckedIds([])
    setIsClickDelete(false)
  }

  const getPreviewUrl = (doc: Document): string => {
    if (doc.published) {
      return `https://${REACT_APP_PUBLISH_URL}/lx/${doc.id}/index.html`
    } else {
      return `https://${REACT_APP_PREVIEW_URL}/${doc.id}/index.html`
    }
  }
  const onClickSort = (key: SortBy) => {
    let ascending = true
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      ascending = false
    }

    switch (key) {
      case SortBy.Topic:
        setOrderByTitle(ascending ? 'ascending' : 'descending')
        break
      case SortBy.PublishedBy:
        setOrderByPublishedBy(ascending ? 'ascending' : 'descending')
        break
      case SortBy.DateTime:
        setOrderByDateTime(ascending ? 'ascending' : 'descending')
        break
      case SortBy.Length:
        setOrderByLength(ascending ? 'ascending' : 'descending')
        break
      default:
        break
    }

    setSortConfig({ key, direction: ascending ? 'ascending' : 'descending' })
  }

  const getCreatedAt = (createdAt: string): string => {
    const x = new Date(createdAt)
    const date = x.getDate()
    const month = x.toLocaleString('default', { month: 'short' })
    const year = x.getFullYear()
    return `${date}\u00A0${month}\u00A0${year}`
  }

  const getCreatedAtTime = (createdAt: string): string => {
    const x = new Date(createdAt)
    const hour = x.getHours()
    const minute = x.getMinutes()
    // Add leading zero
    const hourStr = hour < 10 ? `0${hour}` : `${hour}`
    const minuteStr = minute < 10 ? `0${minute}` : `${minute}`
    return `${hourStr}:${minuteStr}`
  }

  const getTooptipStyle = (key: SortBy): any => {
    switch (key) {
      case SortBy.Topic:
        return orderByTitle === 'ascending' ? tooltipStyleReverse : tooltipStyle
      case SortBy.PublishedBy:
        return orderByPublishedBy === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.DateTime:
        return orderByDateTime === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.Length:
        return orderByLength === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      default:
        return tooltipStyle
    }
  }

  const getSortingText = (key: SortBy): string => {
    switch (key) {
      case SortBy.Topic:
        if (orderByTitle === 'ascending') return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.PublishedBy:
        if (orderByPublishedBy === 'ascending') return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.DateTime:
        if (orderByDateTime === 'ascending') return 'Sort new to old'
        return 'Sort old to new'
      case SortBy.Length:
        if (orderByLength === 'ascending') return 'Sort high to low'
        return 'Sort low to high'
      default:
        return ''
    }
  }

  const onPublish = useCallback(
    async (id: string) => {
      try {
        // Set button to loading state
        const btn = document.getElementById(`btn-publish-${id}`)
        if (btn) {
          btn.classList.add('loading')
          btn.classList.add('disabled')
          // Remove the button text
          btn.innerHTML = ''
        }
        await publish(id)
        // Set button to normal state
        if (btn) {
          btn.classList.remove('loading')
          btn.classList.remove('disabled')
          // Restore the button text
          btn.innerHTML = 'Publish'
        }
        await searchDocuments()
        ToastUtil.success(`Published successfully`)
      } catch (error) {
        ToastUtil.error(`Publish failed`)
      }
    },
    [publish, searchDocuments],
  )

  const updateDocumentStatus = useCallback(
    async (
      docId: string,
      status: keyof Document,
      value: any,
      successMessage: string,
      errorMessage: string,
    ) => {
      try {
        await updateDocument(docId, { [status]: value })
        ToastUtil.success(successMessage)
        setTableTopics((prevDocuments) => {
          return prevDocuments.map((doc) => {
            // If the updated document is the current one, or if the current document is a parent of the updated one
            if (
              doc.id === docId ||
              doc.children?.some((child) => child.id === docId)
            ) {
              // If it's the directly updated document, just update it
              if (doc.id === docId) {
                return { ...doc, [status]: value }
              } else {
                // If it's a parent of the updated document, update the child
                const updatedChildren = doc.children?.map((child) =>
                  child.id === docId ? { ...child, [status]: value } : child,
                )
                return { ...doc, children: updatedChildren }
              }
            } else {
              return doc
            }
          }) as Document[]
        })
      } catch (error) {
        console.error(errorMessage, error)
        ToastUtil.error(errorMessage)
      }
    },
    [updateDocument],
  )
  const openModal = (doc: Document) => {
    setEditingDoc(doc)
    setIsModalOpen(true)
  }

  // Helper function to recursively find a document by ID in tableTopics (including children)
  const findDocumentById = useCallback(
    (docId: string): Document | undefined => {
      const findInDocs = (docs: Document[]): Document | undefined => {
        for (const doc of docs) {
          if (doc.id === docId) return doc
          if (doc.children) {
            const found = findInDocs(doc.children)
            if (found) return found
          }
        }
        return undefined
      }
      return findInDocs(tableTopics)
    },
    [tableTopics],
  )

  const saveNotes = useCallback(
    async (newNotes: string) => {
      if (!editingDoc) return
      await updateDocument(editingDoc.id, { comments: newNotes })

      // Recursively update the document in tableTopics (including nested children)
      const updateDocRecursively = (docs: Document[]): Document[] => {
        return docs.map((doc) => {
          if (doc.id === editingDoc.id) {
            return { ...doc, comments: newNotes }
          }
          if (doc.children) {
            return { ...doc, children: updateDocRecursively(doc.children) }
          }
          return doc
        })
      }

      setTableTopics((currentDocuments) =>
        updateDocRecursively(currentDocuments),
      )
      ToastUtil.success(`Updated successfully`)
      setIsModalOpen(false)
    },
    [editingDoc, updateDocument],
  )

  const toggleAccordion = (docId: string) => {
    setExpandedIds((prevExpandedIds) => {
      if (prevExpandedIds.includes(docId)) {
        // If already expanded, collapse it
        return prevExpandedIds.filter((id) => id !== docId)
      } else {
        // Otherwise, expand it
        return [...prevExpandedIds, docId]
      }
    })
  }

  const getTotalChildren = useCallback((document: Document): number => {
    let total = document.children ? document.children.length : 0
    if (document.children) {
      document.children.forEach((child) => {
        total += getTotalChildren(child)
      })
    }
    return total
  }, [])

  const toRow = useCallback(
    (doc: Document, level: number, nChildren: number) => {
      let isChildRow = doc.parents && doc.parents.length > 0
      if (!isTopicNesting()) {
        isChildRow = level > 0
      }
      return (
        <>
          <tr data-testid="tr-document" key={doc.id}>
            <td className={`${alternateStyle('td', true)} px-1.5`}>
              <div
                className={`flex flex-row items-center justify-between`}
                style={{ paddingLeft: `${level * 20}px` }}
              >
                <div className="flex-grow">
                  {doc.isLocked || !canDeleteCatalog ? (
                    <div
                      className="self-center"
                      style={{
                        border: '2px solid #D5D5DB',
                        minWidth: 18,
                        maxWidth: 18,
                        height: 18,
                        borderRadius: 2,
                        display: 'inline-block',
                      }}
                    />
                  ) : (
                    <input
                      type="checkbox"
                      className="self-center"
                      checked={checkedIds.includes(doc.id)}
                      onChange={(e) => {
                        onChangeCheckbox(e.target.checked, doc.id)
                      }}
                      data-id="checkbox"
                      data-testid={`checkbox-${doc.id}`}
                    />
                  )}
                  <Tooltip
                    id={`title-link-${doc.id}`}
                    style={{ zIndex: 2 }}
                    hidden={!hasArchTopics(doc.id)}
                  >
                    {renderTooltipContentForTopics(doc.id)}
                  </Tooltip>

                  <a
                    data-testid={`topic-link-${doc.id}`}
                    className="mentem-link"
                    href={getPreviewUrl(doc)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      ...(isChildRow && {
                        fontSize: 12,
                      }),
                      verticalAlign: 'super',
                      paddingLeft: '6px',
                    }}
                    data-tooltip-id={`title-link-${doc.id}`}
                    onMouseEnter={() => {
                      fetchDocument(doc.id)
                    }}
                  >
                    {doc.topic || '(No title)'}
                  </a>
                </div>

                {nChildren ? (
                  <div className="flex-none">
                    <button
                      data-testid={`expand-collapse-button-${doc.id}`}
                      onClick={() => toggleAccordion(doc.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {nChildren}
                      <div
                        style={{
                          display: 'inline-block',
                          paddingLeft: '4px',
                          minHeight: '16px',
                          minWidth: '16px',
                          verticalAlign: 'sub',
                        }}
                      >
                        <img
                          className="cursor-pointer"
                          src={
                            expandedIds.includes(doc.id) ? minusSvg : plusSvg
                          }
                          alt="expand"
                          style={{
                            paddingTop: expandedIds.includes(doc.id)
                              ? '7px'
                              : '0',
                            width: '16px',
                          }}
                        />
                      </div>
                    </button>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </td>
            <td className={`${alternateStyle('td')}`}>
              <div className="flex justify-center">
                <img
                  data-testid={`lock-icon-${doc.id}`}
                  src={doc.isLocked ? lockSvg : unlockSvg}
                  data-tooltip-id={`tt-lock-${doc.id}`}
                  data-tooltip-content={doc.isLocked ? 'Locked' : 'Unlocked'}
                  alt={doc.isLocked ? 'Locked' : 'Unlocked'}
                  {...(canUpdateCatalog
                    ? {
                        onClick: () => {
                          updateDocumentStatus(
                            doc.id,
                            'isLocked',
                            !doc.isLocked,
                            'Updated successfully',
                            doc.isLocked
                              ? 'Topic has linked in architecture tool, cannot be unlocked'
                              : 'Failed to update locked status',
                          )
                          setCheckedIds(checkedIds.filter((y) => y !== doc.id))
                        },
                        style: { cursor: 'pointer' },
                      }
                    : {})}
                />
              </div>
            </td>
            {refClient.current === 'unsw' && (
              <td className={`${alternateStyle('td')} w-auto`}>
                <div className="flex items-center justify-center">
                  {doc.time ? `${doc.time} min` : '-'}
                </div>
              </td>
            )}
            <td className={`${alternateStyle('td')} w-auto`}>
              <div className="flex items-center justify-center">
                {doc.publishedUserName || 'Unknown'}
              </div>
            </td>
            <td className={`${alternateStyle('td')} w-auto`}>
              <div className="flex items-center justify-center text-center">
                {getCreatedAt(doc.createdAt)}
                <br />
                {getCreatedAtTime(doc.createdAt)}
              </div>
            </td>
            <td className={`${alternateStyle('td')}`}>
              <div className="flex items-center justify-center">
                <button
                  data-testid={`notes-button-${doc.id}`}
                  onClick={() => {
                    if (canUpdateCatalog) {
                      openModal(doc)
                    } else {
                      ToastUtil.warning('You do not have permission to edit')
                    }
                  }}
                >
                  <img
                    src={doc.comments ? hasNotesSvg : noNotesSvg}
                    alt={doc.comments}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect()
                      setNotesTooltip({
                        visible: true,
                        content: doc.comments || 'Add notes',
                        x: rect.left,
                        y: rect.top,
                      })
                    }}
                    onMouseLeave={() =>
                      setNotesTooltip((prev) => ({ ...prev, visible: false }))
                    }
                  />{' '}
                </button>
              </div>
            </td>
            {hasPublishColumn() && (
              <td className={`${alternateStyle('td')} w-auto`}>
                <div className="flex items-center justify-center">
                  <button
                    data-testid={`btn-publish-${doc.id}`}
                    id={`btn-publish-${doc.id}`}
                    className={StyleUtil.buttonPrimaryUnsw}
                    onClick={() => onPublish(doc.id)}
                  >
                    Publish
                  </button>
                </div>
              </td>
            )}
            <td className={`${alternateStyle('td')} w-auto`}>
              <div className="flex items-center justify-center">
                <button
                  data-testid={`more-options-button-${doc.id}`}
                  onClick={(e) => {
                    setMoreMenuAnchor(e.currentTarget)
                    setMorePopupDocId(doc.id)
                  }}
                >
                  <img src={ellipsisSvg} alt="More options" />
                </button>
              </div>

              <Menu
                data-testid={`more-options-menu-${doc.id}`}
                anchorEl={morePopupDocId === doc.id ? moreMenuAnchor : null}
                open={morePopupDocId === doc.id && Boolean(moreMenuAnchor)}
                onClose={() => {
                  setMoreMenuAnchor(null)
                  setMorePopupDocId('')
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {doc.clientSpecific ? (
                  <MenuItem
                    data-testid={`set-reusable-menuitem-${doc.id}`}
                    onClick={() => {
                      updateDocumentStatus(
                        doc.id,
                        'clientSpecific',
                        false,
                        'Updated successfully',
                        'Failed to update client specific status',
                      )
                      setMoreMenuAnchor(null)
                      setMorePopupDocId('')
                    }}
                  >
                    Set as reusable
                  </MenuItem>
                ) : (
                  <MenuItem
                    data-testid={`set-client-ip-menuitem-${doc.id}`}
                    onClick={() => {
                      updateDocumentStatus(
                        doc.id,
                        'clientSpecific',
                        true,
                        'Updated successfully',
                        'Failed to update client specific status',
                      )
                      setMoreMenuAnchor(null)
                      setMorePopupDocId('')
                    }}
                  >
                    Set as client IP
                  </MenuItem>
                )}
                <MenuItem
                  data-testid={`copy-embed-menuitem-${doc.id}`}
                  onClick={() => {
                    const s3 = `https://${process.env.REACT_APP_PUBLISH_URL}/lx`
                    const previewUrl = `${s3}/${doc.id}/index.html`
                    const embed = `<iframe width="100%" height="1200" src="${previewUrl}" title="Course" frameborder="0" allow="web-share;fullscreen;"></iframe>`
                    navigator.clipboard.writeText(embed)
                    ToastUtil.success('Embed code copied to clipboard')
                    setMoreMenuAnchor(null)
                    setMorePopupDocId('')
                  }}
                >
                  Copy embed code
                </MenuItem>
                <MenuItem
                  data-testid={`copy-html-menuitem-${doc.id}`}
                  onClick={async () => {
                    setMoreMenuAnchor(null)
                    setMorePopupDocId('')
                    try {
                      const s3 = `https://${process.env.REACT_APP_PUBLISH_URL}/lx`
                      const baseUrl = `${s3}/${doc.id}/index.html`
                      const html = await getDocumentHtml(doc.id)
                      const absoluteHtml = resolveRelativeUrls(html, baseUrl)
                      await navigator.clipboard.writeText(absoluteHtml)
                      ToastUtil.success('HTML copied to clipboard')
                    } catch (err) {
                      console.error(err)
                      ToastUtil.error('Failed to copy HTML')
                    }
                  }}
                >
                  Copy HTML
                </MenuItem>
                {canManageTopics() && (
                  <MenuItem
                    data-testid={`copy-to-architecture-menuitem-${doc.id}`}
                    onClick={() => {
                      setCopyDocumentIds([doc.id])
                      setCopyIsEnterprise(doc.theme !== 'unsw')
                      setShowWidgetArchCopy(true)
                      setMoreMenuAnchor(null)
                      setMorePopupDocId('')
                    }}
                  >
                    Copy to architecture
                  </MenuItem>
                )}
                {doc.fileUrl && (
                  <MenuItem
                    data-testid={`download-word-menuitem-${doc.id}`}
                    onClick={() => {
                      window.open(doc.fileUrl, '_blank')
                      setMoreMenuAnchor(null)
                      setMorePopupDocId('')
                    }}
                  >
                    Download Word document
                  </MenuItem>
                )}
              </Menu>
            </td>
            {isDebug() && (
              <td className={`${alternateStyle('td')} w-auto`}>
                <div className="flex items-center justify-center gap-1">
                  <button
                    data-testid={`debug-int-v1-word-${doc.id}`}
                    className={StyleUtil.buttonPrimaryUnsw}
                    onClick={() => downloadIntV1AsWord(doc.id, doc.topic)}
                    title="Recreate Word from intV1"
                  >
                    Word
                  </button>
                  <button
                    data-testid={`debug-int-v1-json-${doc.id}`}
                    className={StyleUtil.buttonPrimaryUnsw}
                    onClick={() => viewIntV1Json(doc.id)}
                    title="View intV1 JSON in a new tab"
                  >
                    JSON
                  </button>
                </div>
              </td>
            )}
          </tr>
        </>
      )
    },
    [
      alternateStyle,
      checkedIds,
      canDeleteCatalog,
      canUpdateCatalog,
      hasArchTopics,
      renderTooltipContentForTopics,
      expandedIds,
      hasPublishColumn,
      morePopupDocId,
      moreMenuAnchor,
      canManageTopics,
      onChangeCheckbox,
      fetchDocument,
      updateDocumentStatus,
      onPublish,
      getDocumentHtml,
      downloadIntV1AsWord,
      viewIntV1Json,
    ],
  )

  const renderRow = useCallback(
    (doc: Document, level = 0): any => {
      return (
        <React.Fragment key={doc.id}>
          {toRow(doc, level, doc.children?.length || 0)}
          {expandedIds.includes(doc.id) &&
            doc.children?.map((childDoc) => {
              return renderRow(childDoc, level + 1)
            })}
        </React.Fragment>
      )
    },
    [toRow, expandedIds],
  )

  const onReloadDocuments = useCallback(
    async (data: { documentIds: string; isLocked: boolean }) => {
      const { documentIds, isLocked } = data
      // Update local state
      setTableTopics((prevDocuments) => {
        return prevDocuments.map((doc) => {
          if (documentIds.includes(doc.id)) {
            return { ...doc, isLocked }
          }
          // Update children
          if (doc.children) {
            const updatedChildren = doc.children.map((child) => {
              if (documentIds.includes(child.id)) {
                return { ...child, isLocked }
              }
              return child
            })
            return { ...doc, children: updatedChildren }
          }
          return doc
        }) as Document[]
      })
    },
    [],
  )

  useEffect(() => {
    Emitter.on(Events.ReloadDocuments, onReloadDocuments)
    return () => {
      Emitter.off(Events.ReloadDocuments, onReloadDocuments)
    }
  }, [onReloadDocuments])

  useEffect(() => {
    if (isLogged) {
      if (showUserTopicsOnly()) {
        setIsFetchedPublishedBy(true)
        // console.log(`user=`, user)
        if (user?.id) {
          refPublishedBy.current = user?.id
        }
      } else {
        fetchPublishedByUsers()
      }
      searchDocuments()
    }
  }, [
    searchDocuments,
    fetchPublishedByUsers,
    isLogged,
    showUserTopicsOnly,
    user,
  ])

  return (
    <>
      <SidebarLayout>
        <div
          id="topics-page"
          data-testid="topics-page"
          className={StyleUtil.containerNoScroll}
          style={{
            overflowY: 'auto',
          }}
        >
          <p className={StyleUtil.headline}>
            {getRole() === Role.SuperAdmin
              ? 'Search or manage topics and pages'
              : 'Search topics, get links or Word documents'}
          </p>
          {isModalOpen && editingDoc && (
            <NotesModal
              key={`notes-modal-${editingDoc.id}-${
                findDocumentById(editingDoc.id)?.comments || ''
              }`}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              initialNotes={findDocumentById(editingDoc.id)?.comments || ''}
              onSave={saveNotes}
            />
          )}
          {!isFetched() && <Spinner />}
          {isFetched() && (
            <div className="flex h-[100%] flex-col pb-10 pt-8" id="THE_PARENT">
              <div
                id="THE_FILTERS"
                className="flex flex-none flex-row items-center"
              >
                <div className="w-1/3" data-testid="search-input-container">
                  <SearchInput
                    onInputChanged={onInputChangedSearch}
                    onEnter={onEnterSearch}
                    isSearching={isSearching}
                  />
                </div>
                <div className="flex-grow"></div>

                <div className="flex-none">
                  <div className="flex gap-3">
                    <DropDownMenu
                      testId="client-filter-dropdown"
                      items={[
                        {
                          id: '0',
                          name: 'All pages',
                          value: '',
                          isLabel: true,
                          default: true,
                        },
                        { id: '1', name: 'Enterprise', value: 'mentem' },
                        { id: '2', name: 'UNSW', value: 'unsw' },
                        {
                          id: '3',
                          name: 'All pages',
                          value: '',
                        },
                      ]}
                      onSelected={onSelectClient}
                    />
                    <DropDownMenu
                      testId="date-filter-dropdown"
                      items={[
                        {
                          id: '0',
                          name: 'Date',
                          value: undefined,
                          default: true,
                          isLabel: true,
                        },
                        { id: '1', name: 'Last 7 days', value: 7 },
                        { id: '2', name: 'Last 30 days', value: 30 },
                        { id: '3', name: 'Last 6 months', value: 183 },
                        {
                          id: '4',
                          name: 'All dates',
                          value: undefined,
                        },
                      ]}
                      onSelected={onSelectDate}
                    />
                    {!showUserTopicsOnly() && (
                      <DropDownMenu
                        testId="published-by-dropdown"
                        items={publishedByOptions}
                        onSelected={onSelectPublishedBy}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div
                id="THE_SUBFILTERS"
                className={`flex items-center justify-start pt-2 ${
                  canDeleteCatalog ? 'pb-6' : 'pb-2'
                }`}
              >
                {isClickDelete && (
                  <>
                    <span className="mr-4 text-[16px]">Are you sure?</span>
                    <button
                      data-testid="btn-delete-from-catalogue-yes"
                      onClick={onConfirmDelete}
                      className={StyleUtil.buttonPrimaryUnsw}
                      disabled={isLoading}
                    >
                      {isLoading ? <ButtonSpinner /> : 'Yes'}
                    </button>
                    <span
                      data-testid="btn-delete-from-catalogue-no"
                      onClick={() => {
                        setIsClickDelete(false)
                        return false
                      }}
                      className="ml-4 cursor-pointer text-[16px] text-secondaryIndigo underline hover:opacity-[0.8]"
                    >
                      Cancel
                    </span>
                  </>
                )}
                {!isClickDelete && canDeleteCatalog && (
                  <span
                    data-testid="btn-delete-from-catalogue"
                    onClick={onClickDelete}
                    className="mentem-link-underline"
                  >
                    Delete from catalogue
                  </span>
                )}
                <div
                  style={{
                    flexGrow: 1,
                  }}
                >
                  <div style={{}} className="flex items-end justify-end">
                    <label
                      data-testid="published-switch-label"
                      style={{
                        fontSize: '14px',
                        fontWeight: 'var(--fw400)',
                        width: '140px',
                      }}
                    >
                      <SwitchPublished
                        data-testid="published-switch"
                        checked={!!refPublished.current}
                        onChange={(e) => {
                          refPublished.current = e.target.checked
                          setCurrPage(1)
                          setTopicsDB([])
                          searchDocuments(1)
                        }}
                        color="default"
                      />
                      {refPublished.current ? 'Published' : 'Preview'}
                    </label>
                    <label
                      data-testid="is-topic-switch-label"
                      style={{
                        fontSize: '14px',
                        fontWeight: 'var(--fw400)',
                        width: '140px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <SwitchIsTopic
                        data-testid="is-topic-switch"
                        checked={!!refIsTopic.current}
                        onChange={(e) => {
                          refIsTopic.current = e.target.checked
                          setCurrPage(1)
                          setTopicsDB([])
                          searchDocuments(1)
                        }}
                        color="default"
                      />
                      {refIsTopic.current ? 'Topics' : 'Non - topics'}
                    </label>
                    <div className="w-1"></div>
                    <label
                      data-testid="client-specific-switch-label"
                      style={{
                        fontSize: '14px',
                        fontWeight: 'var(--fw400)',
                        width: 127,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <SwitchClientSpecific
                        data-testid="client-specific-switch"
                        checked={!!refClientSpecific.current}
                        onChange={(e) => {
                          refClientSpecific.current = e.target.checked
                          setCurrPage(1)
                          setTopicsDB([])
                          searchDocuments(1)
                        }}
                        color="default"
                      />
                      {refClientSpecific.current ? 'Client IP' : 'Reusable'}
                    </label>

                    <div className="w-2"></div>
                  </div>
                </div>
              </div>

              <div className="flex h-[90%] flex-col pt-2">
                {isSearching && <Spinner />}
                {!isSearching && tableTopics.length === 0 && (
                  <div
                    data-testid="no-topics-message"
                    className="flex flex-none justify-center"
                  >
                    <div className="flex flex-none items-center">
                      <span className="mr-4 text-[16px]">
                        No topic(s) found.
                      </span>
                    </div>
                  </div>
                )}
                {!isSearching && tableTopics.length > 0 && (
                  <>
                    <div className="mentem-scrollbar-2 flex-1 overflow-y-auto">
                      <table
                        id="THE_TABLE"
                        data-testid="topics-table"
                        className="mentem-table mentem-table-highlight"
                      >
                        <thead>
                          <tr>
                            <th
                              className={`${alternateStyle(
                                'th',
                                true,
                              )} w-[300px]`}
                              style={{
                                zIndex: 2,
                              }}
                            >
                              <div className="flex flex-row items-center">
                                <span className="grow">Topic title</span>
                                <img
                                  data-testid="sort-topic-button"
                                  className="cursor-pointer"
                                  src={sortSvg}
                                  alt="sort"
                                  onClick={() => onClickSort(SortBy.Topic)}
                                  data-tooltip-id="tooltip-title"
                                />
                              </div>
                            </th>
                            <th className={`${alternateStyle('th')} w-[56px]`}>
                              Lock
                            </th>
                            {refClient.current === 'unsw' && (
                              <th
                                className={`${alternateStyle('th')} w-[80px]`}
                              >
                                <div className="flex flex-row items-center">
                                  <span className="grow">Length</span>
                                  <img
                                    data-testid="sort-length-button"
                                    className="cursor-pointer"
                                    src={sortSvg}
                                    alt="sort"
                                    onClick={() => onClickSort(SortBy.Length)}
                                    data-tooltip-id="tooltip-length"
                                  />
                                </div>
                              </th>
                            )}
                            <th className={`${alternateStyle('th')} w-[87px]`}>
                              <div className="flex flex-row items-center">
                                <span className="grow">Editor</span>
                                <img
                                  data-testid="sort-published-by-button"
                                  className="cursor-pointer"
                                  src={sort2Svg}
                                  alt="sort"
                                  onClick={() =>
                                    onClickSort(SortBy.PublishedBy)
                                  }
                                  data-tooltip-id="tooltip-published-by"
                                />
                              </div>
                            </th>
                            <th className={`${alternateStyle('th')} w-[127px]`}>
                              <div className="flex flex-row items-center">
                                <span className="grow">Date/Time</span>
                                <img
                                  data-testid="sort-date-time-button"
                                  className="cursor-pointer"
                                  src={sortSvg}
                                  alt="sort"
                                  onClick={() => onClickSort(SortBy.DateTime)}
                                  data-tooltip-id="tooltip-date-time"
                                />
                              </div>
                            </th>
                            <th className={`${alternateStyle('th')} w-[60px]`}>
                              Notes
                            </th>
                            {hasPublishColumn() && (
                              <th
                                className={`${alternateStyle('th')} w-[60px]`}
                              >
                                Publish
                              </th>
                            )}
                            <th className={`${alternateStyle('th')} w-[60px]`}>
                              More
                            </th>
                            {isDebug() && (
                              <th
                                className={`${alternateStyle('th')} w-[140px]`}
                              >
                                DEBUG
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedDocuments.map((doc: Document) => {
                            return renderRow(doc, 0)
                          })}
                        </tbody>
                      </table>
                      {!isSearching && nResults > 100 && (
                        <span
                          data-testid="pagination-controls"
                          style={{
                            float: 'right',
                            fontSize: 16,
                            marginTop: 14,
                            marginRight: 20,
                          }}
                        >
                          {nResults === pageSize ? (
                            <button
                              data-testid="show-top-100-button"
                              onClick={() => {
                                setCurrPage(1)
                                setTopicsDB([])
                                setPageSize(100)
                                searchDocuments(1, 100)
                              }}
                            >
                              Top 100 results
                              <img
                                src={minusSvg}
                                alt="contract"
                                style={{
                                  marginLeft: 10,
                                  display: 'inline',
                                  verticalAlign: 'middle',
                                  width: 13,
                                }}
                              />
                            </button>
                          ) : (
                            <button
                              data-testid="show-all-results-button"
                              onClick={() => {
                                setCurrPage(1)
                                setTopicsDB([])
                                setPageSize(nResults)
                                searchDocuments(1, nResults)
                              }}
                            >
                              All results
                              <img
                                src={plusSvg}
                                alt="expand"
                                style={{
                                  marginLeft: 10,
                                  display: 'inline',
                                  verticalAlign: 'baseline',
                                  width: 13,
                                }}
                              />
                            </button>
                          )}
                        </span>
                      )}
                      <Tooltip
                        id="tooltip-title"
                        className="mentem-tooltip"
                        style={getTooptipStyle(SortBy.Topic)}
                        place="top"
                        noArrow={true}
                      >
                        {getSortingText(SortBy.Topic)}
                      </Tooltip>
                      <Tooltip
                        id="tooltip-published-by"
                        className="mentem-tooltip"
                        style={getTooptipStyle(SortBy.PublishedBy)}
                        place="top"
                        noArrow={true}
                      >
                        {getSortingText(SortBy.PublishedBy)}
                      </Tooltip>
                      <Tooltip
                        id="tooltip-date-time"
                        className="mentem-tooltip"
                        style={getTooptipStyle(SortBy.DateTime)}
                        place="top"
                        noArrow={true}
                      >
                        {getSortingText(SortBy.DateTime)}
                      </Tooltip>
                      <Tooltip
                        id="tooltip-length"
                        className="mentem-tooltip"
                        style={getTooptipStyle(SortBy.Length)}
                        place="top"
                        noArrow={true}
                      >
                        {getSortingText(SortBy.Length)}
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </SidebarLayout>
      {showWidgetArchCopy && (
        <div data-testid="widget-arch-copy">
          <WidgetArchCopy
            documentIds={copyDocumentIds}
            isEnterprise={copyIsEnterprise}
            onClose={() => {
              setShowWidgetArchCopy(false)
            }}
          />
        </div>
      )}
      {/* Single, dynamically positioned tooltip for notes. Only one for performance reasons */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: notesTooltip.y + 20,
          left: notesTooltip.x + 10,
          background: '#fff',
          color: '#3C415D',
          border: '0.3px solid #6B69C1',
          fontSize: 12,
          fontWeight: 400,
          borderRadius: 3,
          padding: '3px 6px',

          zIndex: 9999,
          boxShadow: '0px 16px 24px 0px rgba(13, 13, 13, 0.20)',
          pointerEvents: 'none',
          maxWidth: 217,
          whiteSpace: 'pre-wrap',
          transition: 'left 0.05s',
          display: notesTooltip.visible ? 'block' : 'none',
        }}
      >
        {notesTooltip.content}
      </div>
    </>
  )
}

export default Topics
